import querystring from 'query-string';
import equal from 'deep-equal';
import config from '../config';
import { getUrlInfo, getServiceVersion } from '../util/helpers';
import { addEntities, removeEntities } from './entities';
import { invalidSession, limitReached } from './session';
import 'regenerator-runtime/runtime';

export const REQUEST_PENDING = 'REQUEST_PENDING';
export const REQUEST_ERROR = 'REQUEST_ERROR';
export const REQUEST_SUCCESS = 'REQUEST_SUCCESS';
export const REMOVE_REQUEST = 'REMOVE_REQUEST';

export interface RequestPendingAction {
  type: typeof REQUEST_PENDING;
  id: string | number;
  method: string;
  url: string;
  body: any;
  options: any;
  promise: Promise<any>;
}

export interface RequestSuccessAction {
  type: typeof REQUEST_SUCCESS;
  id: string | number;
  method: string;
  url: string;
  body: any;
  responseStatus: number | undefined;
  json: any;
  text: string | undefined;
  options: any;
  promise: Promise<any>;
}

export interface RequestErrorAction {
  type: typeof REQUEST_ERROR;
  id: string | number;
  method: string;
  url: string;
  body: any;
  responseStatus: number | undefined;
  json: any;
  text: string | undefined;
  options: any;
  promise: Promise<any>;
}

export interface RemoveRequestAction {
  type: typeof REMOVE_REQUEST;
  id: string;
}

export type RequestAction =
  | RequestPendingAction
  | RequestSuccessAction
  | RequestErrorAction
  | RemoveRequestAction;

export const STATUS = {
  pending: 'pending',
  success: 'success',
  error: 'error',
} as const;

export interface RequestOptions {
  url: string;
  id?: string | number;
  method?: string;
  data?: any;
  body?: any;
  headers?: Record<string, string>;
  query?: Record<string, any>;
  version?: string;
  controller?: AbortController;
  signal?: AbortSignal;
  dispatchEntities?: boolean;
  abortable?: boolean;
  [key: string]: any;
}

export interface RequestResponse {
  ok: boolean;
  status?: number;
  json?: any;
  text?: string;
  options?: any;
  rawResponse?: Response;
}

const pendingRequestsCache: Record<string, any> = {};
let nextPendingId = 0;

function getQueryObj(query: string): Record<string, any> {
  const urlParams: Record<string, any> = {};
  const pl = /\+/g;
  const search = /([^&=]+)=?([^&]*)/g;
  const decode = function decode(s: string) {
    return decodeURIComponent(s.replace(pl, ' '));
  };

  let match = search.exec(query);
  while (match) {
    const left = decode(match[1]);
    const right = decode(match[2]);
    if (Object.prototype.hasOwnProperty.call(urlParams, left)) {
      if (urlParams[left].constructor !== Array) {
        urlParams[left] = [urlParams[left]];
      }
      urlParams[left].push(right);
    } else {
      urlParams[left] = right;
    }
    match = search.exec(query);
  }

  return urlParams;
}

function splitUrlAndOptions(url: string, options: RequestOptions) {
  const newOptions: RequestOptions = { ...options };
  const split = url.split('?');
  if (split[1]) {
    const query = split.slice(1).join('?');
    if (!newOptions.query) {
      newOptions.query = getQueryObj(query);
    } else {
      newOptions.query = {
        ...newOptions.query,
        ...getQueryObj(query),
      };
    }
  }
  return {
    url: split[0],
    options: newOptions,
  };
}

function getUrlWithQuery(url: string, options: RequestOptions): string {
  const { service } = getUrlInfo(url);
  const version = options.version || getServiceVersion(service);
  if (url.startsWith('http')) {
    return url;
  }
  let result = config.baseUrl + (version ? `/${version}` : '') + url;
  if (options.query && Object.keys(options.query).length > 0) {
    result += result.indexOf('?') === -1 ? '?' : '&';
    result += querystring.stringify(options.query);
  }
  return result;
}

function getOptions(
  method: string,
  url: string,
  data: any,
  options: RequestOptions,
  sessionJSON: any,
): any {
  const requestOptions: any = {
    method,
    headers: options.headers || {},
    rawOptions: { ...options },
  };
  if (sessionJSON && sessionJSON.meta && !requestOptions.headers['x-session']) {
    requestOptions.headers['x-session'] = sessionJSON.meta.id;
  }
  if (['GET', 'DELETE'].indexOf(method) === -1) {
    requestOptions.body = JSON.stringify(data);
  }
  requestOptions.url = getUrlWithQuery(url, options);
  return requestOptions;
}

function requestPending(
  id: string | number,
  method: string,
  url: string,
  body: any,
  options: any,
  promise: Promise<any>,
): RequestPendingAction {
  return {
    type: REQUEST_PENDING,
    id,
    method,
    url,
    body,
    options,
    promise,
  };
}

function requestSuccess(
  id: string | number,
  method: string,
  url: string,
  body: any,
  responseStatus: number | undefined,
  json: any,
  text: string | undefined,
  options: any,
  promise: Promise<any>,
): RequestSuccessAction {
  return {
    type: REQUEST_SUCCESS,
    id,
    method,
    url,
    body,
    responseStatus,
    json,
    text,
    options,
    promise,
  };
}

function requestError(
  id: string | number,
  method: string,
  url: string,
  body: any,
  responseStatus: number | undefined,
  json: any,
  text: string | undefined,
  options: any,
  promise: Promise<any>,
): RequestErrorAction {
  return {
    type: REQUEST_ERROR,
    id,
    method,
    url,
    body,
    responseStatus,
    json,
    text,
    options,
    promise,
  };
}

function dispatchEntitiesAction(
  dispatch: any,
  method: string,
  url: string,
  json: any,
  options: any,
  service: string,
  parent?: any,
) {
  let deleted: any[];

  switch (method) {
    case 'GET':
      dispatch(addEntities(service, json, { reset: false, ...options, parent }));
      break;
    case 'POST':
    case 'PATCH':
    case 'PUT':
      dispatch(addEntities(service, json, { ...options, parent, reset: false }));
      break;
    case 'DELETE':
      deleted = [
        ...(json.deleted || []),
        ...(json.shared_deleted || []),
        ...(json.owned_deleted || []),
      ];
      if (deleted.length > 0) {
        dispatch(removeEntities(service, deleted, { ...options, parent, reset: false }));
      }
      break;
    default:
      /* istanbul ignore next */
      break;
  }
}

function dispatchMethodAction(
  dispatch: any,
  method: string,
  url: string,
  json: any,
  options: any,
) {
  const { service, parent } = getUrlInfo(url);
  if (service === 'document' && url.startsWith('/file/')) {
    dispatchEntitiesAction(dispatch, method, url, json, options, 'file');
  } else if (service !== 'file') {
    dispatchEntitiesAction(
      dispatch,
      method,
      url,
      json,
      options,
      service,
      parent === '' ? undefined : parent,
    );
  }
}

let localRequest = async (options: any): Promise<RequestResponse> => {
  try {
    options.headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (!options.controller || !options.controller.signal) {
      options.controller = new AbortController();
    }
    options.signal = options.controller.signal;
    const response = await fetch(options.url, options);
    try {
      const json = await response.clone().json();
      return {
        ok: response.ok,
        status: response.status,
        json,
        options,
        rawResponse: response,
      };
    } catch (e) {
      const text = await response.clone().text();
      return {
        ok: response.ok,
        status: response.status,
        text,
        options,
        rawResponse: response,
      };
    }
  } catch (e: any) {
    return { ok: false, status: e.status, options };
  }
};

export const request = async (options: any): Promise<RequestResponse> => localRequest(options);

function findRequest(
  url: string,
  method: string,
  data: any,
  options: RequestOptions = { url: '' },
): Promise<any> | undefined {
  for (let i = 0; i < Object.keys(pendingRequestsCache).length; i += 1) {
    const id = Object.keys(pendingRequestsCache)[i];
    const pendingRequest = pendingRequestsCache[id];
    const rUrl = querystring.parseUrl(pendingRequest.url);
    const parsedUrl = querystring.parseUrl(url);
    const rQuery = {
      ...pendingRequest.query,
      ...rUrl.query,
      ...(pendingRequest.options.query || {}),
    };
    const query = options.query ? { ...options.query, ...parsedUrl.query } : parsedUrl.query;
    if (
      pendingRequest.method === method &&
      equal(rUrl.url, parsedUrl.url) &&
      equal(rQuery, query) &&
      equal(pendingRequest.body, data)
    ) {
      return pendingRequest.promise;
    }
  }
  return undefined;
}

export function startRequest(dispatch: any, options: RequestOptions, session: any): Promise<any> {
  let promise: Promise<any> | undefined;
  let pendingId: string | number;
  const { url, id } = options;
  const data = options.data || options.body;
  const method = options.method ? options.method.toUpperCase() : 'GET';
  const result = splitUrlAndOptions(url, options);
  const requestOptions = getOptions(method, url, data, options, session);
  if (id) {
    pendingId = id;
  } else {
    pendingId = nextPendingId;
    nextPendingId += 1;
  }

  const checkResponse = (response: RequestResponse) => {
    if (!response) {
      return response;
    }
    delete pendingRequestsCache[pendingId];
    if (response.ok) {
      if (options.dispatchEntities !== false) {
        dispatchMethodAction(
          dispatch,
          requestOptions.method,
          result.url,
          response.json,
          result.options,
        );
      }
      if (id) {
        dispatch(
          requestSuccess(
            id,
            requestOptions.method,
            result.url,
            data,
            response.status,
            response.json,
            response.text,
            result.options,
            promise as Promise<any>,
          ),
        );
      }
    } else {
      if (response.json) {
        if (response.json.code === 117000000) {
          dispatch(invalidSession());
        } else if (response.json.code === 300098) {
          dispatch(limitReached());
        }
      }
      if (id) {
        dispatch(
          requestError(
            id,
            requestOptions.method,
            result.url,
            data,
            response.status,
            response.json,
            response.text,
            result.options,
            promise as Promise<any>,
          ),
        );
      }
    }
    return response;
  };

  promise = findRequest(url, method, data, options);
  if (!promise) {
    promise = localRequest(requestOptions);
  }
  promise.then(checkResponse).catch(checkResponse);

  result.options.controller = requestOptions.controller;
  const requestPendingObj = requestPending(
    pendingId as string,
    requestOptions.method,
    result.url,
    data,
    result.options,
    promise,
  );
  pendingRequestsCache[pendingId as string] = requestPendingObj;
  if (id) {
    dispatch(requestPendingObj);
  }
  return promise;
}

export function makeRequest(options: RequestOptions = { url: '' }) {
  return (dispatch: any, getState: any) => {
    const state = getState();
    const promise = startRequest(dispatch, options, state.session);
    return promise;
  };
}

export function removeRequest(id: string): RemoveRequestAction {
  return {
    type: REMOVE_REQUEST,
    id,
  };
}

export function overrideRequest(func: typeof localRequest) {
  if (func) {
    localRequest = func;
  }
}

export function cancelAllRequests() {
  Object.keys(pendingRequestsCache).forEach((key) => {
    if (
      pendingRequestsCache[key].options.abortable !== false &&
      pendingRequestsCache[key].options.controller
    ) {
      pendingRequestsCache[key].options.controller.abort();
    }
  });
}
